/**
 * WebAuthn / Passkey Utility Functions
 *
 * Provides client-side helpers for WebAuthn authentication flows:
 * - Passkey registration
 * - Passkey authentication (login)
 * - Credential management
 *
 * Uses axios for API calls to automatically include session cookies
 */

import axios from 'axios';
import { getApiBase } from './api';

const API_BASE_URL = getApiBase();

/**
 * Convert base64url string to ArrayBuffer
 * @param {string} base64url - Base64url encoded string
 * @returns {ArrayBuffer}
 */
function base64urlToBuffer(base64url) {
  // Add padding if needed
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;

  const rawData = atob(base64);
  const buffer = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i++) {
    buffer[i] = rawData.charCodeAt(i);
  }

  return buffer.buffer;
}

/**
 * Convert ArrayBuffer to base64url string
 * @param {ArrayBuffer} buffer - ArrayBuffer to convert
 * @returns {string}
 */
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Check if WebAuthn is supported by the browser
 * @returns {boolean}
 */
export function isWebAuthnSupported() {
  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    navigator.credentials.create &&
    navigator.credentials.get
  );
}

/**
 * Check if platform authenticator (built-in biometrics) is available
 * @returns {Promise<boolean>}
 */
export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.error('Error checking platform authenticator:', error);
    return false;
  }
}

/**
 * Start passkey registration process
 * Fetches registration options from server
 *
 * @returns {Promise<Object>} Registration options from server
 */
export async function startPasskeyRegistration() {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/passkey/register/options`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to start registration');
  }
}

/**
 * Create passkey credential using WebAuthn API
 *
 * @param {Object} options - Registration options from server
 * @returns {Promise<Object>} Created credential
 */
export async function createPasskeyCredential(options) {
  // Convert options to proper format for WebAuthn API
  const serverOptions = options.options;

  // Build publicKeyOptions with only known valid fields
  const publicKeyOptions = {
    challenge: base64urlToBuffer(serverOptions.challenge),
    rp: serverOptions.rp,
    user: {
      id: base64urlToBuffer(serverOptions.user.id),
      name: serverOptions.user.name,
      displayName: serverOptions.user.displayName,
    },
    pubKeyCredParams: serverOptions.pubKeyCredParams,
  };

  // Add optional fields only if they exist
  if (serverOptions.timeout) {
    publicKeyOptions.timeout = serverOptions.timeout;
  }

  if (serverOptions.attestation) {
    publicKeyOptions.attestation = serverOptions.attestation;
  }

  if (serverOptions.authenticatorSelection) {
    publicKeyOptions.authenticatorSelection = serverOptions.authenticatorSelection;
  }

  if (serverOptions.extensions) {
    publicKeyOptions.extensions = serverOptions.extensions;
  }

  // Only include excludeCredentials if it exists and is a non-empty array
  if (Array.isArray(serverOptions.excludeCredentials) && serverOptions.excludeCredentials.length > 0) {
    publicKeyOptions.excludeCredentials = serverOptions.excludeCredentials.map(cred => {
      const mappedCred = {
        type: cred.type,
        id: base64urlToBuffer(cred.id),
      };
      // Only include transports if it's a valid array
      if (Array.isArray(cred.transports) && cred.transports.length > 0) {
        mappedCred.transports = cred.transports;
      }
      return mappedCred;
    });
  }

  try {
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    });

    if (!credential) {
      throw new Error('Failed to create credential');
    }

    // Convert credential to JSON format for server
    return {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64url(credential.response.attestationObject),
        transports: credential.response.getTransports ? credential.response.getTransports() : [],
      },
    };
  } catch (error) {
    console.error('Error creating passkey credential:', error);
    throw error;
  }
}

/**
 * Complete passkey registration by sending credential to server
 *
 * @param {string} challengeId - Challenge ID from registration options
 * @param {Object} credential - Created credential from WebAuthn API
 * @param {string} deviceName - Optional device name
 * @returns {Promise<Object>} Registration result
 */
export async function completePasskeyRegistration(challengeId, credential, deviceName = null) {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/passkey/register/verify`, {
      challenge_id: challengeId,
      credential: credential,
      device_name: deviceName,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to complete registration');
  }
}

/**
 * Register a passkey (full flow)
 *
 * @param {string} deviceName - Optional device name
 * @returns {Promise<Object>} Registration result
 */
export async function registerPasskey(deviceName = null) {
  // Step 1: Get registration options from server
  const optionsData = await startPasskeyRegistration();

  // Step 2: Create credential using WebAuthn API
  const credential = await createPasskeyCredential(optionsData);

  // Step 3: Send credential to server for verification
  const result = await completePasskeyRegistration(
    optionsData.challenge_id,
    credential,
    deviceName
  );

  return result;
}

/**
 * Start passkey authentication process
 * Fetches authentication options from server
 *
 * @param {string} email - User's email (optional for usernameless flow)
 * @returns {Promise<Object>} Authentication options from server
 */
export async function startPasskeyAuthentication(email = null) {
  const url = new URL(`${API_BASE_URL}/auth/passkey/login/options`);
  if (email) {
    url.searchParams.append('email', email);
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to start authentication');
  }

  return await response.json();
}

/**
 * Get passkey credential from WebAuthn API
 *
 * @param {Object} options - Authentication options from server
 * @returns {Promise<Object>} Credential assertion
 */
export async function getPasskeyCredential(options) {
  // Convert options to proper format for WebAuthn API
  const serverOptions = options.options;

  // Build publicKeyOptions with only known valid fields
  const publicKeyOptions = {
    challenge: base64urlToBuffer(serverOptions.challenge),
    rpId: serverOptions.rpId,
  };

  // Add optional fields only if they exist
  if (serverOptions.timeout) {
    publicKeyOptions.timeout = serverOptions.timeout;
  }

  if (serverOptions.userVerification) {
    publicKeyOptions.userVerification = serverOptions.userVerification;
  }

  if (serverOptions.extensions) {
    publicKeyOptions.extensions = serverOptions.extensions;
  }

  // Only include allowCredentials if it exists and is a non-empty array
  if (Array.isArray(serverOptions.allowCredentials) && serverOptions.allowCredentials.length > 0) {
    publicKeyOptions.allowCredentials = serverOptions.allowCredentials.map(cred => {
      const mappedCred = {
        type: cred.type,
        id: base64urlToBuffer(cred.id),
      };
      // Only include transports if it's a valid array
      if (Array.isArray(cred.transports) && cred.transports.length > 0) {
        mappedCred.transports = cred.transports;
      }
      return mappedCred;
    });
  }

  try {
    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    });

    if (!credential) {
      throw new Error('Failed to get credential');
    }

    // Convert credential to JSON format for server
    return {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        authenticatorData: bufferToBase64url(credential.response.authenticatorData),
        signature: bufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null,
      },
    };
  } catch (error) {
    console.error('Error getting passkey credential:', error);
    throw error;
  }
}

/**
 * Complete passkey authentication by sending credential to server
 *
 * @param {string} challengeId - Challenge ID from authentication options
 * @param {Object} credential - Credential assertion from WebAuthn API
 * @returns {Promise<Object>} Authentication result with tokens
 */
export async function completePasskeyAuthentication(challengeId, credential) {
  const response = await fetch(`${API_BASE_URL}/auth/passkey/login/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      challenge_id: challengeId,
      credential: credential,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to complete authentication');
  }

  return await response.json();
}

/**
 * Authenticate with passkey (full flow)
 *
 * @param {string} email - User's email (optional for usernameless flow)
 * @returns {Promise<Object>} Authentication result with tokens
 */
export async function authenticateWithPasskey(email = null) {
  // Step 1: Get authentication options from server
  const optionsData = await startPasskeyAuthentication(email);

  // Step 2: Get credential using WebAuthn API
  const credential = await getPasskeyCredential(optionsData);

  // Step 3: Send credential to server for verification
  const result = await completePasskeyAuthentication(
    optionsData.challenge_id,
    credential
  );

  return result;
}

/**
 * Get list of user's registered passkeys
 *
 * @returns {Promise<Array>} List of credentials
 */
export async function getPasskeyCredentials() {
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/passkey/credentials`);
    return response.data.credentials;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get credentials');
  }
}

/**
 * Delete a passkey credential
 *
 * @param {string} credentialId - Credential ID to delete
 * @returns {Promise<Object>} Deletion result
 */
export async function deletePasskeyCredential(credentialId) {
  try {
    const response = await axios.delete(`${API_BASE_URL}/auth/passkey/credentials/${credentialId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete credential');
  }
}

/**
 * Check if user has registered passkeys
 *
 * @param {string} email - User's email
 * @returns {Promise<boolean>} True if user has passkeys
 */
export async function hasPasskeyCredentials(email) {
  const response = await fetch(
    `${API_BASE_URL}/auth/passkey/has-credentials?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );

  if (!response.ok) {
    // Don't throw error - return false for better UX
    return false;
  }

  const data = await response.json();
  return data.has_credentials;
}
